/**
 * ChartModal — Reusable fullscreen chart expansion modal.
 *
 * Renders a portal-based overlay with:
 *  - Chart type switcher (bar / line / area / horizontal-bar)
 *  - Sort, Top-N filter, search
 *  - PNG / CSV export
 *  - Responsive chart area
 *
 * Props:
 *   open        {bool}     — controlled open state
 *   onClose     {fn}       — close handler
 *   title       {string}   — modal header title
 *   children    {fn}       — render prop: (chartType, processedData) => JSX
 *                            receives the active chart type + filtered/sorted data
 *   data        {array}    — raw data array (used for filtering/sorting/export)
 *   labelKey    {string}   — key used as the X/Y label (e.g. 'month', 'label')
 *   supportedTypes {array} — chart types to show in switcher (default: all)
 *   defaultType {string}   — initial chart type
 *   stats       {array}    — optional [{label, value}] summary stats shown above chart
 *   chartRef    {ref}      — forwarded ref to the chart wrapper div (for PNG export)
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import './ChartModal.css';

// ── Chart type definitions ────────────────────────────────────────────────────
export const CHART_TYPES = {
  bar:      { icon: '▦',  label: 'Bar Chart'             },
  line:     { icon: '📈', label: 'Line Chart'            },
  area:     { icon: '◭',  label: 'Area Chart'            },
  hbar:     { icon: '▬',  label: 'Horizontal Bar'        },
  pie:      { icon: '◕',  label: 'Pie / Donut Chart'     },
};

const ALL_TYPES = Object.keys(CHART_TYPES);

const TOP_N_OPTIONS = [
  { value: 0,   label: 'Show All' },
  { value: 5,   label: 'Top 5'   },
  { value: 10,  label: 'Top 10'  },
  { value: 15,  label: 'Top 15'  },
  { value: 20,  label: 'Top 20'  },
];

// ── CSV export helper ─────────────────────────────────────────────────────────
function exportCSV(data, filename = 'chart-data.csv') {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const rows    = data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','));
  const csv     = [headers.join(','), ...rows].join('\n');
  const blob    = new Blob([csv], { type: 'text/csv' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── PNG export helper — captures the full modal body (stats + chart) ──────────
async function exportPNG(bodyEl, filename = 'chart.png') {
  if (!bodyEl) return;
  try {
    // Temporarily remove overflow clipping so html2canvas captures full content
    const prevOverflow = bodyEl.style.overflow;
    const prevHeight   = bodyEl.style.height;
    const prevMaxH     = bodyEl.style.maxHeight;
    bodyEl.style.overflow  = 'visible';
    bodyEl.style.height    = 'auto';
    bodyEl.style.maxHeight = 'none';

    // Also expand the parent modal dialog so nothing clips
    const modal     = bodyEl.closest('.cmodal');
    let prevModalMH = '';
    let prevModalOF = '';
    if (modal) {
      prevModalMH = modal.style.maxHeight;
      prevModalOF = modal.style.overflow;
      modal.style.maxHeight = 'none';
      modal.style.overflow  = 'visible';
    }

    // Small delay to let layout reflow
    await new Promise(r => setTimeout(r, 80));

    const canvas = await html2canvas(bodyEl, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      width:  bodyEl.scrollWidth,
      height: bodyEl.scrollHeight,
      windowWidth:  bodyEl.scrollWidth,
      windowHeight: bodyEl.scrollHeight,
    });

    // Restore styles
    bodyEl.style.overflow  = prevOverflow;
    bodyEl.style.height    = prevHeight;
    bodyEl.style.maxHeight = prevMaxH;
    if (modal) {
      modal.style.maxHeight = prevModalMH;
      modal.style.overflow  = prevModalOF;
    }

    const a = document.createElement('a');
    a.href     = canvas.toDataURL('image/png');
    a.download = filename;
    a.click();
  } catch (e) {
    console.warn('[ChartModal] PNG export failed:', e);
  }
}

// ── Main component ────────────────────────────────────────────────────────────
const ChartModal = ({
  open,
  onClose,
  title        = 'Chart',
  children,
  data         = [],
  labelKey     = 'label',
  valueKey     = 'ach_pct',
  supportedTypes = ALL_TYPES,
  defaultType  = 'bar',
  stats        = [],
  exportFilename = 'chart-data',
}) => {
  const [chartType, setChartType]   = useState(defaultType);
  const [sortDir,   setSortDir]     = useState('desc');   // 'asc' | 'desc' | 'none'
  const [topN,      setTopN]        = useState(0);        // 0 = show all
  const [search,    setSearch]      = useState('');
  const [bodyEl,    setBodyEl]      = useState(null);

  // Reset controls when modal opens
  useEffect(() => {
    if (open) {
      setChartType(defaultType);
      setSortDir('desc');
      setTopN(0);
      setSearch('');
    }
  }, [open, defaultType]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // ── Process data: search → sort → slice ──────────────────────────────────
  const processedData = useMemo(() => {
    if (!data?.length) return [];
    let result = [...data];

    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(row =>
        String(row[labelKey] ?? '').toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortDir !== 'none') {
      result.sort((a, b) => {
        const av = Number(a[valueKey] ?? 0);
        const bv = Number(b[valueKey] ?? 0);
        return sortDir === 'desc' ? bv - av : av - bv;
      });
    }

    // Top N
    if (topN > 0) result = result.slice(0, topN);

    return result;
  }, [data, search, sortDir, topN, labelKey, valueKey]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!open) return null;

  const modal = (
    <div className="cmodal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label={title}>
      <div className="cmodal">

        {/* ── Header ── */}
        <div className="cmodal__header">
          <span className="cmodal__title">{title}</span>
          <div className="cmodal__header-actions">
            <button
              className="cmodal__action-btn"
              onClick={() => exportCSV(processedData, `${exportFilename}.csv`)}
              title="Export CSV"
            >
              ⬇ CSV
            </button>
            <button
              className="cmodal__action-btn"
              onClick={() => exportPNG(bodyEl, `${exportFilename}.png`)}
              title="Export PNG"
            >
              🖼 PNG
            </button>
            <button className="cmodal__close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="cmodal__toolbar">

          {/* Chart type switcher */}
          <div className="cmodal__toolbar-group">
            <span className="cmodal__toolbar-label">Type</span>
            {supportedTypes.map(type => (
              <button
                key={type}
                className={`cmodal__type-btn ${chartType === type ? 'cmodal__type-btn--active' : ''}`}
                onClick={() => setChartType(type)}
                title={CHART_TYPES[type]?.label}
              >
                {CHART_TYPES[type]?.icon}
              </button>
            ))}
          </div>

          <div className="cmodal__toolbar-divider" />

          {/* Sort */}
          <div className="cmodal__toolbar-group">
            <span className="cmodal__toolbar-label">Sort</span>
            <select
              className="cmodal__select"
              value={sortDir}
              onChange={e => setSortDir(e.target.value)}
            >
              <option value="desc">High → Low</option>
              <option value="asc">Low → High</option>
              <option value="none">Original</option>
            </select>
          </div>

          {/* Top N */}
          <div className="cmodal__toolbar-group">
            <span className="cmodal__toolbar-label">Show</span>
            <select
              className="cmodal__select"
              value={topN}
              onChange={e => setTopN(Number(e.target.value))}
            >
              {TOP_N_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="cmodal__toolbar-divider" />

          {/* Search */}
          <input
            className="cmodal__search"
            type="text"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {/* Record count */}
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {processedData.length} record{processedData.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Body ── */}
        <div className="cmodal__body" ref={setBodyEl}>

          {/* Optional summary stats */}
          {stats.length > 0 && (
            <div className="cmodal__stats">
              {stats.map((s, i) => (
                <div key={i} className="cmodal__stat">
                  <span className="cmodal__stat-label">{s.label}</span>
                  <span className="cmodal__stat-value" style={s.color ? { color: s.color } : {}}>
                    {s.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Chart render prop */}
          <div className="cmodal__chart-area">
            {typeof children === 'function'
              ? children(chartType, processedData)
              : children
            }
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ChartModal;
